import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContractStateDto,
  ContractTaskStatus,
  PersonDto,
  useListContractsQuery,
  useListPersonsQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import type { EntityTableColumn } from "../../../../common/entity-table/EntityTable";
import { type ExtraFilterChip, FilterBar } from "../../../../common/entity-table/FilterBar";
import { displayPersonName } from "../../../../common/persons/displayPersonName";
import { MultiSelectOption } from "../../view-all/filtering/MultiSelectFilter";
import { OPTIONS_PAGE_SIZE, usePagedOptions } from "./usePagedOptions";

const STATUSES = [ContractTaskStatus.Open, ContractTaskStatus.Completed];
const RESPONSIBLE = "responsible";
const CONTRACT = "contract";

const asOption = ({ value, label }: MultiSelectOption) => ({ value, label });

interface Props {
  selectedResponsiblePersons: PersonDto[];
  onSelectedResponsiblePersonsChange: (persons: PersonDto[]) => void;
  selectedContracts: MultiSelectOption[];
  onSelectedContractsChange: (values: MultiSelectOption[]) => void;
  selectedStatuses: MultiSelectOption[];
  onSelectedStatusesChange: (values: MultiSelectOption[]) => void;
  extraChips: ExtraFilterChip[];
  onResetAll: () => void;
  countText: string | null;
}

export const TaskFilterBar = ({
  selectedResponsiblePersons,
  onSelectedResponsiblePersonsChange,
  selectedContracts,
  onSelectedContractsChange,
  selectedStatuses,
  onSelectedStatusesChange,
  extraChips,
  onResetAll,
  countText,
}: Props) => {
  const { t } = useTranslation();
  const orgId = useLoggedInWithOrgContextState().selectedOrg.id;
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [personSearch, setPersonSearch] = useState("");
  const [personSkip, setPersonSkip] = useState(0);
  const [contractSearch, setContractSearch] = useState("");
  const [contractSkip, setContractSkip] = useState(0);

  const handleDropdownOpen = useCallback((dropdownKey: string) => setOpenDropdown(dropdownKey), []);
  const handleDropdownClose = useCallback(
    (dropdownKey: string) => setOpenDropdown((open) => (open === dropdownKey ? null : open)),
    [],
  );

  const persons = useListPersonsQuery(
    openDropdown === `${RESPONSIBLE}-filter`
      ? { orgIds: [orgId], name: personSearch || undefined, limit: OPTIONS_PAGE_SIZE, skip: personSkip }
      : skipToken,
  );
  const contracts = useListContractsQuery(
    openDropdown === `${CONTRACT}-filter`
      ? {
          orgId,
          state: ContractStateDto.Published,
          title: contractSearch || undefined,
          limit: OPTIONS_PAGE_SIZE,
          skip: contractSkip,
        }
      : skipToken,
  );
  const personOptions = usePagedOptions(personSkip, persons.currentData?.persons, persons.currentData?.count);
  const contractOptions = usePagedOptions(contractSkip, contracts.currentData?.contracts);

  const searchPersons = useCallback((term: string) => {
    setPersonSearch(term);
    setPersonSkip(0);
  }, []);
  const searchContracts = useCallback((term: string) => {
    setContractSearch(term);
    setContractSkip(0);
  }, []);

  const knownPersons = [...selectedResponsiblePersons, ...personOptions.items];
  const columns: EntityTableColumn[] = [
    {
      key: RESPONSIBLE,
      label: t("Responsible"),
      multiSelectConfig: {
        options: personOptions.items.map((person) => ({ value: person.id, label: displayPersonName(person) })),
        selectedValues: selectedResponsiblePersons.map((person) => ({
          value: person.id,
          label: displayPersonName(person),
        })),
        onSelect: (values) =>
          onSelectedResponsiblePersonsChange(
            values.flatMap((value) => knownPersons.find((person) => person.id === value.value) ?? []),
          ),
        onInputChange: searchPersons,
        optionsCount: personOptions.count,
        onLoadMore: () => setPersonSkip(personOptions.items.length),
        isLoading: persons.isFetching,
        isError: persons.isError,
        onRetry: () => void persons.refetch(),
      },
    },
    {
      key: CONTRACT,
      label: t("Contract"),
      multiSelectConfig: {
        options: contractOptions.items.map((contract) => ({ value: contract.id, label: contract.title })),
        selectedValues: selectedContracts.map(asOption),
        onSelect: (values) => onSelectedContractsChange(values.map(asOption)),
        onInputChange: searchContracts,
        optionsCount: contractOptions.count,
        onLoadMore: () => setContractSkip(contractOptions.items.length),
        isLoading: contracts.isFetching,
        isError: contracts.isError,
        onRetry: () => void contracts.refetch(),
      },
    },
    {
      key: "status",
      label: t("Status"),
      multiSelectConfig: {
        options: STATUSES.map((status) => ({ value: status, label: t(status) })),
        selectedValues: selectedStatuses.map(asOption),
        onSelect: (values) => onSelectedStatusesChange(values.map(asOption)),
      },
    },
  ];

  return (
    <FilterBar
      columns={columns}
      extraChips={extraChips}
      onResetAll={onResetAll}
      countText={countText}
      onDropdownOpen={handleDropdownOpen}
      onDropdownClose={handleDropdownClose}
    />
  );
};
